import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  Eye,
  ThumbsUp,
  MessageCircle,
  HelpCircle,
  MessageSquare,
  Megaphone,
  Calendar,
  User,
  CheckCircle
} from 'lucide-react'

interface PostDetail {
  id: number
  title: string
  body: string
  excerpt: string
  post_type: 'question' | 'discussion' | 'announcement'
  author_id: number
  author_name: string
  author_specialty: string | null
  community_id: number
  community_name: string | null
  category_id: number | null
  category_name: string | null
  likes_count: number
  comments_count: number
  views_count: number
  has_solution: boolean
  created_at: string
  updated_at: string
  was_edited: boolean
}

interface Stats {
  views_count: number
  likes_count: number
  comments_count: number
}

interface ShowProps {
  post: PostDetail
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-700'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-yellow-100 text-yellow-800'
  }
}

export default function Show({ post, stats }: ShowProps) {
  const typeConfig = POST_TYPE_CONFIG[post.post_type]
  const TypeIcon = typeConfig.icon

  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer la publication "${post.title}" ?`)) {
      router.delete(`/admin/posts/${post.id}`)
    }
  }

  return (
    <AdminLayout
      title={post.title}
      breadcrumbs={[
        { label: 'Publications', href: '/admin/posts' },
        { label: post.title }
      ]}
    >
      {/* Actions */}
      <div className="flex items-center gap-3 mb-6">
        <Link href={`/posts/${post.id}`} target="_blank">
          <Button variant="secondary" className="flex items-center gap-2">
            <Eye size={16} />
            Voir sur le site
          </Button>
        </Link>
        <Link href={`/admin/posts/${post.id}/edit`}>
          <Button variant="secondary" className="flex items-center gap-2">
            <Edit size={16} />
            Modifier
          </Button>
        </Link>
        <Button
          variant="secondary"
          className="flex items-center gap-2 text-red-600 hover:bg-red-50"
          onClick={handleDelete}
        >
          <Trash2 size={16} />
          Supprimer
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Post content */}
          <Card className="p-6">
            {/* Badges */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${typeConfig.className}`}>
                <TypeIcon size={14} />
                {typeConfig.label}
              </span>
              {post.category_name && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  {post.category_name}
                </span>
              )}
              {post.has_solution && post.post_type === 'question' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircle size={14} />
                  Resolu
                </span>
              )}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-text-dark mb-4">{post.title}</h2>

            {/* Body */}
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">{post.body}</p>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <Card className="p-4">
            <h3 className="font-semibold text-text-dark mb-4">Statistiques</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mx-auto mb-2">
                  <Eye size={20} className="text-blue-600" />
                </div>
                <p className="text-xl font-bold text-text-dark">{stats.views_count}</p>
                <p className="text-xs text-text-gray">Vues</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mx-auto mb-2">
                  <ThumbsUp size={20} className="text-green-600" />
                </div>
                <p className="text-xl font-bold text-text-dark">{stats.likes_count}</p>
                <p className="text-xs text-text-gray">Likes</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mx-auto mb-2">
                  <MessageCircle size={20} className="text-purple-600" />
                </div>
                <p className="text-xl font-bold text-text-dark">{stats.comments_count}</p>
                <p className="text-xs text-text-gray">Commentaires</p>
              </div>
            </div>
          </Card>

          {/* Details */}
          <Card className="p-4">
            <h3 className="font-semibold text-text-dark mb-4">Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <User size={16} className="text-text-gray mt-0.5" />
                <div>
                  <p className="text-text-gray">Auteur</p>
                  <p className="font-medium text-text-dark">{post.author_name}</p>
                  {post.author_specialty && (
                    <p className="text-text-gray text-xs">{post.author_specialty}</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-text-gray mt-0.5" />
                <div>
                  <p className="text-text-gray">Cree le</p>
                  <p className="font-medium text-text-dark">{formatDate(post.created_at)}</p>
                </div>
              </div>
              {post.was_edited && (
                <div className="flex items-start gap-3">
                  <Edit size={16} className="text-text-gray mt-0.5" />
                  <div>
                    <p className="text-text-gray">Modifie le</p>
                    <p className="font-medium text-text-dark">{formatDate(post.updated_at)}</p>
                  </div>
                </div>
              )}
              {post.community_name && (
                <div className="flex items-start gap-3">
                  <MessageSquare size={16} className="text-text-gray mt-0.5" />
                  <div>
                    <p className="text-text-gray">Communaute</p>
                    <p className="font-medium text-text-dark">{post.community_name}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
